import { useQuery } from '@tanstack/react-query';

import { withGlobalError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { ChartReference } from '../types';

import { queryKeys } from './query-keys';

interface HelmSearch {
  entries: Entries;
}

interface Entries {
  [key: string]: { version: string; appVersion: string }[];
}

export interface ChartVersion {
  chart?: string;
  repo: string;
  label?: string;
  version: string;
  registryId?: number;
  appVersion?: string;
}

/**
 * React hook to get a list of available versions for a chart from specified repositories
 *
 * @param chartReference The chart path and chart source (repo or registry)
 * @param staleTime Stale time for the query
 * @param useCache Whether to use the cache for the query
 */
export function useHelmRepoVersions<T = ChartVersion[]>(
  {
    staleTime,
    select,
  }: { staleTime: number; select?: (data: ChartVersion[]) => T },
  chartReference?: ChartReference,
  useCache: boolean = true,
  showError = true
) {
  //  useEnvironmentId returns 0 if not set - change this '0' to undefined so it's ommited from the query params
  const environmentId = useEnvironmentId() || undefined;
  const hasRegistryOrRepo =
    !!chartReference?.repoURL || !!chartReference?.registryID;
  const hasChartPath = !!chartReference?.chartPath;

  return useQuery({
    queryKey: queryKeys.chartVersions(
      chartReference?.registryID ?? '',
      chartReference?.chartPath ?? '',
      useCache
    ),
    queryFn: () =>
      getSearchHelmRepo(
        {
          repo: chartReference?.repoURL,
          chart: chartReference?.chartPath ?? '',
          useCache,
          registryId: chartReference?.registryID,
          environmentId,
        },
        chartReference?.repoURL
      ),
    staleTime,
    select,
    enabled: hasRegistryOrRepo && hasChartPath,
    ...(showError &&
      withGlobalError(
        `Unable to retrieve versions from ${
          chartReference?.repoURL ?? chartReference?.registryID
        }`
      )),
  });
}

type SearchRepoParams = {
  repo?: string;
  registryId?: number;
  chart: string;
  useCache?: boolean;
  environmentId?: number;
};

/**
 * Get Helm repositories for user
 */
export async function getSearchHelmRepo(
  params: SearchRepoParams,
  registryUrl?: string
): Promise<ChartVersion[]> {
  if (!params.chart) {
    throw new Error('Chart path is required');
  }
  if (!params.repo && !params.registryId) {
    throw new Error('Repo or registry ID is required');
  }

  try {
    const { data } = await axios.get<HelmSearch>(`templates/helm`, {
      params,
    });
    // if separated by '/', take the last part
    const chartKey = params.chart.split('/').pop() || params.chart;
    const versions = data.entries[chartKey];
    return (
      versions?.map((v) => ({
        repo: params.repo ?? registryUrl ?? '',
        registryId: params.registryId,
        registryUrl,
        chart: params.chart,
        version: v.version,
        appVersion: v.appVersion,
      })) ?? []
    );
  } catch (err) {
    throw parseAxiosError(err, 'Unable to retrieve helm repositories for user');
  }
}
